import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  BackrsRole,
  BackrsUser,
  ContentCompetencyProficiency,
  ContentNugget,
  ContentNuggetBookmark,
  NUGGET_VARIANTS,
} from '@backrs_ui/common-ui';

import {
  fetchProficienciesForAllCompetencies,
  postProficiencyForCompetency,
} from './thunks/competencyProficiency';
import {
  postContentNuggetBookmark,
  fetchAllContentNuggetBookmarks,
  deleteContentNuggetBookmark,
} from './thunks/nuggetBookmark';
import {
  postContentNuggetCompleted,
  ContentNuggetCompleted,
  filterCompletedNuggetIds,
  fetchAllContentNuggetCompleted,
} from './thunks/nuggetCompleted';
import { fetchUserViaAutoLogin } from './thunks/user.thunk';
import { checkUserResume } from './thunks/userDocument';
import { handlePending, handleRejected } from '../reducers';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type UserState = Readonly<{
  user?: BackrsUser;
  skippedSignIn: boolean;
  isGuidanceCounselor: boolean;
  loading: boolean;
  error?: string;
}>;

const initialState: UserState = {
  user: undefined,
  isGuidanceCounselor: false,
  skippedSignIn: false,
  loading: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<BackrsUser>) => {
      state.user = action.payload;
    },
    skipSignIn: (state) => {
      state.skippedSignIn = true;
    },
    setIsGuidanceCounselor: (state, action: PayloadAction<boolean>) => {
      state.isGuidanceCounselor = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * AUTO LOGIN
       */
      .addCase(fetchUserViaAutoLogin.pending, handlePending)
      .addCase(fetchUserViaAutoLogin.rejected, handleRejected)
      .addCase(
        fetchUserViaAutoLogin.fulfilled,
        (state, action: PayloadAction<BackrsUser | null>) => {
          if (!action.payload) {
            state.loading = false;

            return;
          }

          state.user = action.payload;
          state.loading = false;
        },
      )

      /**
       * NUGGET BOOKMARKS
       */

      // FETCH
      .addCase(fetchAllContentNuggetBookmarks.pending, handlePending)
      .addCase(fetchAllContentNuggetBookmarks.rejected, handleRejected)
      .addCase(
        fetchAllContentNuggetBookmarks.fulfilled,
        (state, action: PayloadAction<ContentNuggetBookmark[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.bookmarks = action.payload;
          }
        },
      )

      // POST
      .addCase(postContentNuggetBookmark.pending, handlePending)
      .addCase(postContentNuggetBookmark.rejected, handleRejected)
      .addCase(
        postContentNuggetBookmark.fulfilled,
        (state, action: PayloadAction<ContentNuggetBookmark[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.bookmarks = action.payload;
          }
        },
      )
      // DELETE
      .addCase(deleteContentNuggetBookmark.pending, handlePending)
      .addCase(deleteContentNuggetBookmark.rejected, handleRejected)
      .addCase(deleteContentNuggetBookmark.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          if (state.user) {
            state.user.bookmarks = action.payload;
          }
        }
      })

      /**
       * NUGGET COMPLETED
       */

      // FETCH
      .addCase(fetchAllContentNuggetCompleted.pending, handlePending)
      .addCase(fetchAllContentNuggetCompleted.rejected, handleRejected)
      .addCase(
        fetchAllContentNuggetCompleted.fulfilled,
        (state, action: PayloadAction<ContentNuggetCompleted[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.completed_nugget_ids = filterCompletedNuggetIds(action.payload);
          }
        },
      )

      // POST
      .addCase(postContentNuggetCompleted.pending, handlePending)
      .addCase(postContentNuggetCompleted.rejected, handleRejected)
      .addCase(
        postContentNuggetCompleted.fulfilled,
        (state, action: PayloadAction<ContentNuggetCompleted[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.completed_nugget_ids = filterCompletedNuggetIds(action.payload);
          }
        },
      )

      /**
       * COMPETENCY PROFICIENCY
       */

      // FETCH
      .addCase(fetchProficienciesForAllCompetencies.pending, handlePending)
      .addCase(fetchProficienciesForAllCompetencies.rejected, handleRejected)
      .addCase(
        fetchProficienciesForAllCompetencies.fulfilled,
        (state, action: PayloadAction<ContentCompetencyProficiency[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.competency_proficiencies = action.payload;
          }
        },
      )

      // POST
      .addCase(postProficiencyForCompetency.pending, handlePending)
      .addCase(postProficiencyForCompetency.rejected, handleRejected)
      .addCase(
        postProficiencyForCompetency.fulfilled,
        (state, action: PayloadAction<ContentCompetencyProficiency[]>) => {
          state.loading = false;
          if (state.user) {
            state.user.competency_proficiencies = action.payload;
          }
        },
      )

      /**
       * USER DOCUMENTS
       */
      .addCase(checkUserResume.pending, handlePending)
      .addCase(checkUserResume.rejected, handleRejected)
      .addCase(checkUserResume.fulfilled, (state, action: PayloadAction<boolean>) => {
        state.loading = false;
        if (state.user) {
          state.user.hasResume = action.payload;
        }
      })

      /**
       * LOGOUT
       */
      .addCase(logout, (state) => {
        state.user = undefined;
        state.loading = false;
        state.error = undefined;
      });
  },
});

export const { skipSignIn, setUser, setIsGuidanceCounselor } = userSlice.actions;

// Reducer
export const userReducer = userSlice.reducer;

// Selectors
export const isAuthenticated = (state: RootState) => !!state.user?.user;

export const selectUser = (state: RootState) => state.user?.user;

export const selectUserId = (state: RootState): number | undefined => {
  return state.user.user?.id;
};

export const selectDoesUserHaveAccessToken = (state: RootState): boolean => {
  if (state.user.user) {
    return !!state.user.user.access_token;
  }

  return false;
};

export const selectProfilePhoto = (state: RootState) => {
  return state.user.user?.profile_image;
};

export const selectHasResume = (state: RootState) => state.user.user?.hasResume ?? false;

export const selectDoesUserHaveRole = (role: BackrsRole) => (state: RootState) => {
  const user = selectUser(state);

  return user?.roles?.some((r) => r.name === role) ?? false;
};

export const selectBookmarkedNuggetId =
  ({ nuggetId, variant }: { nuggetId?: number; variant?: ContentNugget['variant'] }) =>
  (state: RootState) => {
    if (!state.user?.user?.bookmarks || !nuggetId || !variant) {
      return undefined;
    }

    const bookmarks = state.user.user.bookmarks;

    switch (variant) {
      case NUGGET_VARIANTS.POST:
        return bookmarks.find((b) => b.content_post?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.SCHOLARSHIPS:
        return bookmarks.find((b) => b.content_scholarship?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.INTERNSHIP:
        return bookmarks.find((b) => b.content_internship?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.PROFILE_INTERNAL:
        return bookmarks.find((b) => b.content_profile?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.PROFILE_EXTERNAL:
        return bookmarks.find((b) => b.content_external_profile?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.CAREER_PATH:
        return bookmarks.find((b) => b.content_career_path?.id === nuggetId)?.id;

      case NUGGET_VARIANTS.INSTITUTION:
        if (bookmarks.find((b) => b.content_college?.id === nuggetId)) {
          return bookmarks.find((b) => b.content_college?.id === nuggetId)?.id;
        }

        return bookmarks.find((b) => b.content_nugget?.id === nuggetId)?.id;

      default: // for all other nuggets
        return bookmarks.find((b) => b.content_nugget?.id === nuggetId)?.id;
    }
  };
